/**
 * Timers that keep running when the tab is hidden (ARCHITECTURE §6 "Timing").
 *
 * Browsers throttle `setInterval` in background tabs to ≥ 1 s (and Chrome's intensive throttling to once a
 * minute), and stop requestAnimationFrame entirely. An online match must keep exchanging packets and keep its
 * sim ticking while hidden, so on the web timers run inside a tiny dedicated Worker (injected: `createWorker`, see
 * src/loop/browser-scheduler.ts in apps/client), whose timers are not throttled that way. Without a worker
 * (native shells, tests, Node, strict CSP) it uses setInterval.
 *
 * The returned object satisfies @hf/netcode's `NetScheduler` structurally (`now`, `every`).
 */

export interface IntervalScheduler {
  /** Monotonic ms. */
  now(): number;
  /** Call `fn` every `ms` until the returned cancel function is called. */
  every(ms: number, fn: () => void): () => void;
  /** True if timers survive background throttling (worker-backed). */
  readonly background: boolean;
  dispose(): void;
}

/** The part of a dedicated Worker the scheduler uses (web: a Blob-URL worker, see src/loop/browser-scheduler.ts). */
export interface WorkerLike {
  postMessage(data: { op: 'start' | 'stop'; id: number; ms?: number }): void;
  onmessage: ((e: { data: number }) => void) | null;
  onerror: ((e: unknown) => void) | null;
  terminate(): void;
}

export interface SchedulerDeps {
  now?: () => number;
  /**
   * Create the timer worker (protocol: `{op:'start',id,ms}` / `{op:'stop',id}` in, the timer id out). Default: none,
   * i.e. setInterval (native shells and Node have no throttled background tabs; the web passes its Blob worker).
   */
  createWorker?: () => WorkerLike | null;
}

export const createIntervalScheduler = (deps: SchedulerDeps = {}): IntervalScheduler => {
  const now = deps.now ?? (() => performance.now());
  let worker: WorkerLike | null = null;
  try {
    worker = deps.createWorker?.() ?? null;
  } catch {
    worker = null;
  }
  const callbacks = new Map<number, () => void>();
  let nextId = 1;
  if (worker) {
    worker.onmessage = (e) => {
      const fn = callbacks.get(e.data);
      if (fn) fn();
    };
    worker.onerror = () => {
      // Worker died (CSP, crash): move every live timer to setInterval.
      const w = worker;
      worker = null;
      w?.terminate();
      for (const [id, fn] of callbacks) fallback.set(id, setInterval(fn, intervals.get(id) ?? 50));
    };
  }
  const intervals = new Map<number, number>();
  const fallback = new Map<number, ReturnType<typeof setInterval>>();
  return {
    now,
    get background() {
      return worker !== null;
    },
    every(ms, fn) {
      const id = nextId++;
      const period = Math.max(1, ms);
      callbacks.set(id, fn);
      intervals.set(id, period);
      if (worker) worker.postMessage({ op: 'start', id, ms: period });
      else fallback.set(id, setInterval(fn, period));
      return () => {
        if (!callbacks.delete(id)) return;
        intervals.delete(id);
        if (worker) worker.postMessage({ op: 'stop', id });
        const t = fallback.get(id);
        if (t !== undefined) clearInterval(t);
        fallback.delete(id);
      };
    },
    dispose() {
      for (const t of fallback.values()) clearInterval(t);
      fallback.clear();
      callbacks.clear();
      intervals.clear();
      worker?.terminate();
      worker = null;
    },
  };
};

/** Page visibility as an injectable source (tests flip it by hand). */
export interface VisibilitySource {
  readonly hidden: boolean;
  subscribe(fn: () => void): () => void;
}
